import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../../form/Form.css';
import './ParticipantGroupEditor.css';
import api from '../../wrapper/API';
import Popup from '../../popup/Popup';
import ActionButton from '../../input/ActionButton';
import ParticipantGroupDot from '../ParticipantGroupDot';
import GroupStatusBadge from './GroupStatusBadge';
import GroupGeneralTab from './GroupGeneralTab';
import GroupMembershipTab from './GroupMembershipTab';
import GroupSignupTab from './GroupSignupTab';
import GroupPricesTab from './GroupPricesTab';
import GroupMailRulesTab from './GroupMailRulesTab';
import { usePopup } from '../../context/PopupContext';
import { useSuccessBar } from '../../context/SuccessBarContext';
import {
  apiErrorMessage, createDefaults, editorTabs, ERROR_TAB, formFromGroup, pricesDirty, sameForm, sameFormValue,
  TAB, TAB_LABEL, toSaveRequest, validateForm,
} from './groupAdminUtils';

// Form fields per tab (unsaved-change marker on the tab).
const TAB_FIELDS = {
  [TAB.general]: ['name', 'pluralName', 'description', 'colorKey'],
  [TAB.membership]: ['validityMode'],
  [TAB.signup]: ['signupChannel', 'selfServiceBooking', 'selfServiceAddOns', 'selfServiceIntro'],
  [TAB.prices]: ['defaultEntryPrice'],
  [TAB.mails]: [],
};

const sameRules = (a, b) => {
  if (!a || !b) return true;
  return a.length === b.length && a.every((r, i) =>
    r.slot === b[i].slot && r.enabled === b[i].enabled && (r.textblockId ?? null) === (b[i].textblockId ?? null));
};

/**
 * Editor of one participant group (tabs Allgemein · Mitgliedschaft · Anmeldung · Preise · Mails; guest
 * without Mitgliedschaft) or of a new group (group = null). One "Speichern": PUT Update (POST Create) and,
 * if changed, PUT {id}/MailRules. Archive/unarchive/delete live in the header.
 * The page keys this component by group, so switching groups always starts with a fresh form.
 * applyGroup(rawGroup) patches a mutation response into the shared list (→ normalized group | null);
 * refreshGroups() refetches the list (only when a response carries no group).
 */
function ParticipantGroupEditor({
  group, groups, guest, isOffline, refreshGroups, applyGroup, showError,
  onBack, onCreated, onArchived, onDeleted, onDirtyChange,
  mailRulesCache, textblocks, ensureTextblocks,
}) {
  const isCreate = !group;
  const isGuest = group?.isGuest === true;
  const groupId = group?.id ?? null;
  const tabs = editorTabs(isGuest);
  const { openPopup, closePopup } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();

  // Set in the effect body as well: StrictMode runs mount → cleanup → mount in development.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const [createBaseline] = useState(() => createDefaults(groups));
  const baseline = useMemo(() => (group ? formFromGroup(group) : createBaseline), [group, createBaseline]);
  const [form, setForm] = useState(baseline);
  const [tab, setTab] = useState(TAB.general);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [lifecycleBusy, setLifecycleBusy] = useState(false);

  // Mail rules: loaded once per group and page visit (cache owned by the page), edited as a draft.
  const cachedRules = groupId != null ? mailRulesCache.get(groupId) ?? null : null;
  const [rulesStatus, setRulesStatus] = useState(cachedRules ? 'loaded' : 'idle');
  const [rules, setRules] = useState(cachedRules);
  const [rulesDraft, setRulesDraft] = useState(cachedRules);

  const copySource = isCreate && form.copyFromId
    ? groups.find(g => String(g.id) === form.copyFromId) ?? null
    : null;
  const copyCandidates = useMemo(() => groups.filter(g => !g.isArchived), [groups]);

  const formDirty = !sameForm(form, baseline);
  const rulesDirty = !sameRules(rulesDraft, rules);
  const dirty = formDirty || rulesDirty;
  const unsavedPrices = !isCreate && pricesDirty(form, baseline);

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const errors = validateForm(form, { groups, groupId, copySource });
  const shownErrors = submitted ? errors : {};
  const errorTabs = new Set(Object.keys(shownErrors).map(key => ERROR_TAB[key]));
  const changedTabs = new Set(isCreate ? [] : tabs.filter(t =>
    (t === TAB.mails ? rulesDirty : TAB_FIELDS[t].some(key => !sameFormValue(key, form[key], baseline[key])))));

  const setField = useCallback((key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setSaveError('');
  }, []);

  // ── Mail rules ──────────────────────────────────────────────────────────────────────────────────
  // Guard against a second request while one is running (StrictMode re-runs effects in development).
  const rulesRequested = useRef(cachedRules != null);
  const loadRules = useCallback(() => {
    if (groupId == null) return;
    rulesRequested.current = true;
    setRulesStatus('loading');
    api.get(`/ParticipantGroup/${groupId}/MailRules`)
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : [];
        mailRulesCache.set(groupId, list);
        if (!alive.current) return;
        setRules(list);
        setRulesDraft(list);
        setRulesStatus('loaded');
      })
      .catch(() => {
        rulesRequested.current = false;
        if (alive.current) setRulesStatus('error');
      });
  }, [groupId, mailRulesCache]);

  // Needed by the tab "Mails" and by the "Sofort gebucht" warning in the tab "Anmeldung".
  const needRules = !isCreate && (tab === TAB.mails
    || (tab === TAB.signup && form.signupChannel === 'PersonalLink' && form.selfServiceBooking === 'Immediate'));

  useEffect(() => {
    if (needRules && rulesStatus === 'idle' && !isOffline && !rulesRequested.current) loadRules();
  }, [needRules, rulesStatus, isOffline, loadRules]);

  useEffect(() => {
    if (tab === TAB.mails && !isCreate) ensureTextblocks();
  }, [tab, isCreate, ensureTextblocks]);

  const changeRule = useCallback((slot, patch) => {
    setRulesDraft(prev => (prev ?? []).map(r => (r.slot === slot ? { ...r, ...patch } : r)));
    setSaveError('');
  }, []);

  // ── Save / discard ──────────────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (saving || isOffline) return;
    setSubmitted(true);
    const firstError = Object.keys(errors)[0];
    if (firstError) {
      const errorTab = ERROR_TAB[firstError];
      if (errorTab && tabs.includes(errorTab)) setTab(errorTab);
      return;
    }

    setSaving(true);
    setSaveError('');
    const payload = toSaveRequest(form, group, copySource);

    if (isCreate) {
      startSuccessBarLoading('Gruppe wird angelegt');
      try {
        const res = await api.post('/ParticipantGroup/Create', {
          ...payload,
          copySettingsFromParticipantGroupId: copySource ? copySource.id : null,
        });
        if (!applyGroup(res.data?.group)) await refreshGroups();
        startSuccessBarSuccess('Gruppe angelegt');
        // Opens the created group in the editor (this instance unmounts).
        onCreated(res.data?.group?.id ?? null);
      } catch (err) {
        closeSuccessBar();
        if (alive.current) {
          setSaveError(apiErrorMessage(err, 'Die Gruppe konnte nicht angelegt werden.'));
          setSaving(false);
        }
      }
      return;
    }

    startSuccessBarLoading('Gruppe wird gespeichert');
    if (formDirty) {
      try {
        const res = await api.put(`/ParticipantGroup/Update/${groupId}`, payload);
        // The response is the saved group: server-normalized values (trimmed texts, rounded prices) become
        // the new form state.
        const updated = applyGroup(res.data) ?? (await refreshGroups())?.find(g => g.id === groupId);
        if (updated && alive.current) setForm(formFromGroup(updated));
      } catch (err) {
        closeSuccessBar();
        if (alive.current) {
          setSaveError(apiErrorMessage(err, 'Die Gruppe konnte nicht gespeichert werden.'));
          setSaving(false);
        }
        return;
      }
    }
    if (rulesDirty) {
      try {
        const res = await api.put(`/ParticipantGroup/${groupId}/MailRules`, {
          rules: rulesDraft.map(r => ({ slot: r.slot, enabled: r.enabled, textblockId: r.textblockId ?? null })),
        });
        const list = Array.isArray(res.data) ? res.data : rulesDraft;
        mailRulesCache.set(groupId, list);
        if (alive.current) {
          setRules(list);
          setRulesDraft(list);
        }
      } catch (err) {
        closeSuccessBar();
        if (alive.current) {
          setSaveError(`Die Mail-Regeln wurden nicht gespeichert: ${apiErrorMessage(err, 'unbekannter Fehler.')}`);
          setSaving(false);
        }
        return;
      }
    }
    startSuccessBarSuccess('Gruppe gespeichert');
    if (alive.current) {
      setSaving(false);
      setSubmitted(false);
    }
  };

  const handleDiscard = () => {
    setForm(baseline);
    setRulesDraft(rules);
    setSubmitted(false);
    setSaveError('');
  };

  // ── Archive / unarchive / delete ────────────────────────────────────────────────────────────────
  // groupOf(res): the changed group in the response (patched into the list); without one → refetch.
  const runLifecycle = ({ request, groupOf, loading, success, errorTitle, fallback, onSuccess }) => {
    closePopup();
    setLifecycleBusy(true);
    startSuccessBarLoading(loading);
    request()
      .then(async (res) => {
        if (!(groupOf && applyGroup(groupOf(res)))) await refreshGroups();
        startSuccessBarSuccess(success);
        onSuccess?.();
      })
      .catch(err => {
        closeSuccessBar();
        showError(errorTitle, apiErrorMessage(err, fallback));
        // The row was probably stale (e.g. in use meanwhile) → bring the list in line with the server.
        if (err?.response) refreshGroups();
      })
      .finally(() => { if (alive.current) setLifecycleBusy(false); });
  };

  const requestArchive = () => {
    openPopup(Number(group.currentMemberCount ?? 0) > 0 ? 'groupcannotarchive' : 'confirmarchivegroup', groupId);
  };

  const handleArchive = () => runLifecycle({
    request: () => api.put(`/ParticipantGroup/Archive/${groupId}`),
    groupOf: res => res.data,
    loading: 'Gruppe wird archiviert',
    success: 'Gruppe archiviert',
    errorTitle: 'Archivieren fehlgeschlagen',
    fallback: 'Die Gruppe konnte nicht archiviert werden.',
    onSuccess: () => onArchived?.(groupId),
  });

  const handleUnarchive = () => runLifecycle({
    request: () => api.put(`/ParticipantGroup/Unarchive/${groupId}`),
    groupOf: res => res.data?.group,
    loading: 'Gruppe wird wiederhergestellt',
    success: 'Gruppe wiederhergestellt',
    errorTitle: 'Wiederherstellen fehlgeschlagen',
    fallback: 'Die Gruppe konnte nicht wiederhergestellt werden.',
  });

  const handleDelete = () => runLifecycle({
    request: () => api.delete(`/ParticipantGroup/Delete/${groupId}`),
    loading: 'Gruppe wird gelöscht',
    success: 'Gruppe gelöscht',
    errorTitle: 'Löschen fehlgeschlagen',
    fallback: 'Die Gruppe konnte nicht gelöscht werden.',
    onSuccess: () => onDeleted?.(groupId),
  });

  const handleTabKeyDown = (event, index) => {
    const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const next = tabs[(index + step + tabs.length) % tabs.length];
    setTab(next);
    document.getElementById(`groupEditorTab-${next}`)?.focus();
  };

  // ── Render ──────────────────────────────────────────────────────────────────────────────────────
  const headerDot = { id: groupId ?? 0, name: form.name || 'Neue Gruppe', colorKey: form.colorKey, isGuest };
  const canDelete = !isCreate && !group.systemKey && !group.isInUse;
  const lifecycleDisabled = isOffline || lifecycleBusy || saving;

  let panel;
  switch (tab) {
    case TAB.membership:
      panel = (
        <GroupMembershipTab
          form={form} setField={setField} group={group} guest={guest} isCreate={isCreate}
          isOffline={isOffline} refreshGroups={refreshGroups} showError={showError}
        />
      );
      break;
    case TAB.signup:
      panel = (
        <GroupSignupTab
          form={form} setField={setField} errors={shownErrors} group={group} isCreate={isCreate}
          baseline={baseline} mailRules={rulesStatus === 'loaded' ? rulesDraft : null}
          onOpenMails={() => setTab(TAB.mails)}
        />
      );
      break;
    case TAB.prices:
      panel = (
        <GroupPricesTab
          form={form} setField={setField} errors={shownErrors} group={group} isCreate={isCreate}
          copySource={copySource} unsavedPrices={unsavedPrices} isOffline={isOffline} showError={showError}
        />
      );
      break;
    case TAB.mails:
      panel = (
        <GroupMailRulesTab
          group={group} isCreate={isCreate} copySource={copySource} rulesStatus={rulesStatus} draft={rulesDraft}
          onChangeRule={changeRule} onRetry={loadRules} textblocks={textblocks} isOffline={isOffline}
        />
      );
      break;
    default:
      panel = (
        <GroupGeneralTab
          form={form} setField={setField} errors={shownErrors} group={group} isCreate={isCreate}
          copyCandidates={copyCandidates} copySource={copySource}
        />
      );
  }

  return (
    <section className="groupEditor" aria-label={isCreate ? 'Neue Gruppe' : `Gruppe ${group.name}`}>
      <button type="button" className="groupEditorBack" onClick={onBack}>
        <span className="material-symbols-rounded" aria-hidden="true">arrow_back</span>
        Alle Gruppen
      </button>

      <div className="groupEditorHeader">
        <div className="groupEditorTitleRow">
          <span className="groupEditorTitleDot">
            <ParticipantGroupDot participantGroup={headerDot} size="md" withTooltip={false} />
          </span>
          <h3 className="groupEditorTitle">{isCreate ? 'Neue Gruppe' : group.name}</h3>
          {isGuest && <GroupStatusBadge kind="system" />}
          {group?.isArchived && <GroupStatusBadge kind="archived" />}
        </div>
        {!isCreate && !isGuest && (
          <div className="groupEditorHeaderActions">
            {group.isArchived ? (
              <ActionButton
                icon="unarchive" label="Wiederherstellen" type={3}
                onClick={() => openPopup('confirmunarchivegroup', groupId)}
                disabled={lifecycleDisabled}
              />
            ) : (
              <ActionButton icon="archive" label="Archivieren" onClick={requestArchive} disabled={lifecycleDisabled} />
            )}
            {canDelete && (
              <ActionButton
                icon="delete" label="Löschen" type={1}
                onClick={() => openPopup('confirmdeletegroup', groupId)}
                disabled={lifecycleDisabled}
              />
            )}
          </div>
        )}
      </div>

      {isOffline && !isCreate && !isGuest && (
        <p className="formFieldHint groupEditorHeaderHint">Archivieren und Löschen sind offline nicht verfügbar.</p>
      )}
      {group?.isArchived && (
        <p className="groupEditorNotice">
          <span className="material-symbols-rounded" aria-hidden="true">inventory_2</span>
          Archiviert – wird beim Zuweisen, in Prozessvorlagen und Rundmails nicht mehr angeboten. Bestehende
          Teilnahmen und Verläufe behalten die Gruppe.
        </p>
      )}

      <div className="groupEditorTabs" role="tablist" aria-label="Einstellungen der Gruppe">
        {tabs.map((t, index) => {
          const selected = t === tab;
          const hasError = errorTabs.has(t);
          return (
            <button
              key={t}
              id={`groupEditorTab-${t}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls="groupEditorPanel"
              tabIndex={selected ? 0 : -1}
              className={`groupEditorTab${selected ? ' isActive' : ''}`}
              onClick={() => setTab(t)}
              onKeyDown={e => handleTabKeyDown(e, index)}
            >
              {TAB_LABEL[t]}
              {hasError && <span className="groupEditorTabMarker isError" title="Enthält Fehler" aria-label="enthält Fehler" />}
              {!hasError && changedTabs.has(t) && (
                <span className="groupEditorTabMarker isChanged" title="Ungespeicherte Änderungen" aria-label="geändert" />
              )}
            </button>
          );
        })}
      </div>

      <div className="groupEditorPanel" role="tabpanel" id="groupEditorPanel" aria-labelledby={`groupEditorTab-${tab}`}>
        {panel}
      </div>

      <div className="groupEditorFooter">
        {saveError && <p className="newError groupEditorSaveError" role="alert">{saveError}</p>}
        {submitted && Object.keys(errors).length > 0 && (
          <p className="groupEditorFieldError">Bitte die markierten Angaben prüfen.</p>
        )}
        <div className="groupEditorFooterBar">
          <span className="groupEditorFooterStatus">
            {isOffline ? (
              <span className="formOfflineHint">
                <span className="material-symbols-rounded formOfflineHintIcon" aria-hidden="true">cloud_off</span>
                Offline nicht verfügbar
              </span>
            ) : (dirty && !isCreate) ? (
              <span className="groupEditorDirty">Ungespeicherte Änderungen</span>
            ) : null}
          </span>
          <div className="groupEditorFooterActions">
            {isCreate && <ActionButton label="Abbrechen" onClick={onBack} disabled={saving} />}
            {!isCreate && dirty && <ActionButton label="Verwerfen" onClick={handleDiscard} disabled={saving} />}
            <ActionButton
              icon={isCreate ? 'add' : 'check'}
              label={isCreate ? 'Gruppe anlegen' : 'Speichern'}
              type={3}
              onClick={handleSave}
              disabled={isOffline || saving || (!isCreate && !dirty)}
            />
          </div>
        </div>
      </div>

      {!isCreate && (
        <React.Fragment>
          <Popup
            name="confirmarchivegroup"
            headerText="Gruppe archivieren"
            confirmText="Archivieren"
            type="warning"
            onConfirm={handleArchive}
            withCancel
          >
            <p>Soll die Gruppe <b>{group.name}</b> archiviert werden?</p>
            <p className="formFieldHint">
              Sie wird danach beim Zuweisen, in Prozessvorlagen und Rundmails nicht mehr angeboten. Bestehende
              Teilnahmen und Verläufe bleiben erhalten; wiederherstellen ist jederzeit möglich.
            </p>
          </Popup>
          <Popup
            name="groupcannotarchive"
            headerText="Archivieren nicht möglich"
            confirmText="Zu Mitgliedschaft"
            type="warning"
            onConfirm={() => setTab(TAB.membership)}
            withCancel
          >
            <p>
              <b>{group.name}</b> hat noch {group.currentMemberCount === 1
                ? '1 heute gültige Mitgliedschaft'
                : `${group.currentMemberCount} heute gültige Mitgliedschaften`}.
            </p>
            <p className="formFieldHint">
              Beende zuerst alle Mitgliedschaften (Tab „Mitgliedschaft“) mit einem Datum vor heute – danach lässt sich
              die Gruppe archivieren.
            </p>
          </Popup>
          <Popup
            name="confirmunarchivegroup"
            headerText="Gruppe wiederherstellen"
            confirmText="Wiederherstellen"
            onConfirm={handleUnarchive}
            withCancel
          >
            <p>Soll die Gruppe <b>{group.name}</b> wiederhergestellt werden?</p>
            <p className="formFieldHint">
              Sie wird wieder überall angeboten. Alle zukünftigen Spieltage bekommen eine Preiszeile mit den
              Standardpreisen (bzw. den Preisen der Prozessvorlage).
            </p>
          </Popup>
          <Popup
            name="confirmdeletegroup"
            headerText="Gruppe löschen"
            confirmText="Löschen"
            type="confirmaction"
            onConfirm={handleDelete}
            withCancel
          >
            <p>Soll die Gruppe <b>{group.name}</b> dauerhaft gelöscht werden?</p>
            <p className="formFieldHint">Löschen ist nur möglich, solange die Gruppe nirgends verwendet wird.</p>
          </Popup>
        </React.Fragment>
      )}
    </section>
  );
}

export default ParticipantGroupEditor;
